// Fichier: src/pages/StudentEvolutionForm.js
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { db } from '../firebase';
import { collection, getDocs, doc, getDoc, setDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Grid,
  Paper,
  CircularProgress,
  Autocomplete,
  Chip,
  Slider,
  Divider,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const TRACK_OPTIONS = [
  { value: 'leisure',     label: 'Loisir' },
  { value: 'foundation',  label: 'Foundation' },
  { value: 'performance', label: 'Performance' },
  { value: 'competition', label: 'Compétition' },
  { value: 'freestyle',   label: 'Freestyle' },
  { value: 'intensive',   label: 'Intensif' },
];

const MOTIVATION_OPTIONS = [
  'Technique', 'Chorégraphie', 'Freestyle', 'Compétition',
  'Performance scène', 'Création artistique', 'Voyages / événements',
  'Vidéo / concept', 'Travail en groupe',
];

const EXPERIENCE_OPTIONS = [
  'Concours', 'Battle', 'Voyage', 'Stage intensif', 'Vidéo concept',
  'Scène / spectacle', 'Collaboration', 'Training freestyle', 'Duo / trio project',
];

const SKILLS = [
  { key: 'technique',       label: 'Technique' },
  { key: 'musicality',      label: 'Musicalité' },
  { key: 'freestyle',       label: 'Freestyle' },
  { key: 'memorization',    label: 'Mémorisation chorégraphique' },
  { key: 'stagePresence',   label: 'Présence scénique' },
  { key: 'confidence',      label: 'Confiance' },
  { key: 'energyIntention', label: 'Énergie / intention' },
  { key: 'learningSpeed',   label: "Rapidité d'apprentissage" },
  { key: 'precision',       label: 'Précision' },
  { key: 'endurance',       label: 'Endurance' },
  { key: 'versatility',     label: 'Polyvalence' },
  { key: 'discipline',      label: 'Discipline / régularité' },
  { key: 'personalWork',    label: 'Travail personnel' },
];

const emptySkillMap = () => {
  const map = {};
  SKILLS.forEach((s) => {
    map[s.key] = { current: 0, target: 0, coachComment: '', studentComment: '' };
  });
  return map;
};

const emptyGoal   = () => ({ title: '', type: 'technique', description: '', deadline: '', progress: 0, status: 'not_started', visibleToStudent: true });
const emptyAction = () => ({ objective: '', action: '', deadline: '', done: false });

const StudentEvolutionForm = () => {
  const navigate  = useNavigate();
  const { id }    = useParams();
  const isEditing = Boolean(id);

  const [loading, setLoading]                   = useState(true);
  const [students, setStudents]                 = useState([]);
  const [studentId, setStudentId]               = useState('');
  const [studentName, setStudentName]           = useState('');
  const [season, setSeason]                     = useState('2026-2027');
  const [track, setTrack]                       = useState('performance');
  const [whyDance, setWhyDance]                 = useState('');
  const [motivations, setMotivations]           = useState([]);
  const [experiences, setExperiences]           = useState([]);
  const [inspirations, setInspirations]         = useState('');
  const [longTermGoal, setLongTermGoal]         = useState('');
  const [artisticIdentity, setArtisticIdentity] = useState('');
  const [skills, setSkills]                     = useState(emptySkillMap());
  const [strengths, setStrengths]               = useState('');
  const [improvements, setImprovements]         = useState('');
  const [seasonGoals, setSeasonGoals]           = useState([emptyGoal()]);
  const [actionPlan, setActionPlan]             = useState([emptyAction()]);
  const [studentNotes, setStudentNotes]         = useState('');
  const [privateCoachNotes, setPrivateCoachNotes] = useState('');

  useEffect(() => { fetchInitialData(); }, [id]);

  const getFullName = (user) => `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || user.id;

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const users = usersSnapshot.docs.map((d) => ({
        id: d.id, ...d.data(),
        fullName: getFullName({ id: d.id, ...d.data() }),
      }));
      setStudents(users);

      if (isEditing) {
        const profileDoc = await getDoc(doc(db, 'student_evolution_profiles', id));
        if (!profileDoc.exists()) { navigate('/student-evolutions'); return; }

        const data = profileDoc.data();
        setStudentId(data.studentId || id);
        setStudentName(data.studentName || '');
        setSeason(data.season || '2026-2027');
        setTrack(data.track || 'performance');
        setWhyDance(data.vision?.whyDance || '');
        setMotivations(data.vision?.motivations || []);
        setExperiences(data.vision?.experiences || []);
        setInspirations(data.vision?.inspirations || '');
        setLongTermGoal(data.vision?.longTermGoal || '');
        setArtisticIdentity(data.vision?.artisticIdentity || '');
        setSkills({ ...emptySkillMap(), ...(data.skills || {}) });
        setStrengths(Array.isArray(data.strengths) ? data.strengths.join('\n') : (data.strengths || ''));
        setImprovements(Array.isArray(data.improvements) ? data.improvements.join('\n') : (data.improvements || ''));
        setSeasonGoals(
          (data.seasonGoals || [])
            .map((g) => ({ ...emptyGoal(), ...g, deadline: g.deadline?.toDate ? g.deadline.toDate().toISOString().slice(0, 10) : (g.deadline || '') }))
            .concat((data.seasonGoals || []).length ? [] : [emptyGoal()])
        );
        setActionPlan((data.actionPlan || []).length ? data.actionPlan : [emptyAction()]);
        setStudentNotes(data.studentNotes || '');
        setPrivateCoachNotes(data.privateCoachNotes || '');
      }
    } catch (error) {
      console.error('Erreur chargement formulaire suivi:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStudentChange = (_, selected) => {
    setStudentId(selected?.id || '');
    setStudentName(selected?.fullName || '');
  };

  const updateSkill  = (key, field, value) => setSkills((prev) => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
  const updateGoal   = (index, field, value) => setSeasonGoals((prev) => prev.map((g, i) => (i === index ? { ...g, [field]: value } : g)));
  const updateAction = (index, field, value) => setActionPlan((prev) => prev.map((a, i) => (i === index ? { ...a, [field]: value } : a)));
  const toList       = (text) => text.split('\n').map((x) => x.trim()).filter(Boolean);

  const cleanGoals = () => seasonGoals
    .filter((g) => g.title.trim())
    .map((g) => ({
      ...g,
      progress: Number(g.progress) || 0,
      deadline: g.deadline ? Timestamp.fromDate(new Date(g.deadline)) : null,
    }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!studentId) { alert("Choisis un élève avant d'enregistrer."); return; }

    const docId   = isEditing ? id : studentId;
    const payload = {
      studentId, studentName, season, track,
      vision: { whyDance, motivations, experiences, inspirations, longTermGoal, artisticIdentity },
      skills,
      strengths:    toList(strengths),
      improvements: toList(improvements),
      seasonGoals:  cleanGoals(),
      actionPlan:   actionPlan.filter((a) => a.objective.trim() || a.action.trim()),
      studentNotes,
      privateCoachNotes,
      updatedAt: serverTimestamp(),
      createdAt: isEditing ? undefined : serverTimestamp(),
    };

    Object.keys(payload).forEach((key) => payload[key] === undefined && delete payload[key]);

    try {
      await setDoc(doc(db, 'student_evolution_profiles', docId), payload, { merge: true });
      navigate(`/student-evolutions/${docId}`);
    } catch (error) {
      console.error('Erreur sauvegarde suivi individuel:', error);
      alert('Erreur lors de la sauvegarde. Regarde la console.');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={60} sx={{ color: '#7C3AED' }} />
      </Box>
    );
  }

  const selectedStudent = students.find((s) => s.id === studentId) || null;

  return (
    <Box sx={{ p: 3, maxWidth: 1250, mx: 'auto' }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold', color: '#7C3AED' }}>
          {isEditing ? 'Modifier le suivi individuel' : 'Créer un suivi individuel'}
        </Typography>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>

            {/* Élève / Saison / Track */}
            <Grid item xs={12} md={5}>
              <Autocomplete
                disabled={isEditing}
                options={students}
                value={selectedStudent}
                getOptionLabel={(option) => option.fullName || option.email || option.id}
                onChange={handleStudentChange}
                renderInput={(params) => <TextField {...params} label="Élève" required />}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField label="Saison" value={season} onChange={(e) => setSeason(e.target.value)} fullWidth required />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Track</InputLabel>
                <Select value={track} label="Track" onChange={(e) => setTrack(e.target.value)}>
                  {TRACK_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>

            {/* 1. Vision */}
            <Grid item xs={12}>
              <Typography variant="h6">1. Vision de la danse</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Pourquoi l'élève danse ?" value={whyDance} onChange={(e) => setWhyDance(e.target.value)} fullWidth multiline rows={4} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Objectif long terme" value={longTermGoal} onChange={(e) => setLongTermGoal(e.target.value)} fullWidth multiline rows={4} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                multiple options={MOTIVATION_OPTIONS} value={motivations}
                onChange={(_, value) => setMotivations(value)}
                renderTags={(value, getTagProps) => value.map((option, index) => <Chip label={option} {...getTagProps({ index })} />)}
                renderInput={(params) => <TextField {...params} label="Motivations" />}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                multiple options={EXPERIENCE_OPTIONS} value={experiences}
                onChange={(_, value) => setExperiences(value)}
                renderTags={(value, getTagProps) => value.map((option, index) => <Chip label={option} {...getTagProps({ index })} />)}
                renderInput={(params) => <TextField {...params} label="Expériences souhaitées" />}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Inspirations" value={inspirations} onChange={(e) => setInspirations(e.target.value)} fullWidth multiline rows={3} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Identité artistique" value={artisticIdentity} onChange={(e) => setArtisticIdentity(e.target.value)} fullWidth multiline rows={3} />
            </Grid>

            <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>

            {/* 2. Matrice de compétences */}
            <Grid item xs={12}>
              <Typography variant="h6">2. Matrice de compétences</Typography>
            </Grid>
            {SKILLS.map((skill) => (
              <Grid item xs={12} md={6} key={skill.key}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography sx={{ fontWeight: 'bold', mb: 1 }}>{skill.label}</Typography>
                  <Typography variant="body2">Niveau actuel : {skills[skill.key]?.current || 0}/10</Typography>
                  <Slider value={skills[skill.key]?.current || 0} min={0} max={10} step={1} onChange={(_, v) => updateSkill(skill.key, 'current', v)} />
                  <Typography variant="body2">Objectif : {skills[skill.key]?.target || 0}/10</Typography>
                  <Slider value={skills[skill.key]?.target || 0} min={0} max={10} step={1} onChange={(_, v) => updateSkill(skill.key, 'target', v)} />
                  <TextField
                    label="Commentaire coach"
                    value={skills[skill.key]?.coachComment || ''}
                    onChange={(e) => updateSkill(skill.key, 'coachComment', e.target.value)}
                    fullWidth size="small" multiline rows={2}
                  />
                </Paper>
              </Grid>
            ))}

            <Grid item xs={12} md={6}>
              <TextField label="Forces actuelles, une par ligne" value={strengths} onChange={(e) => setStrengths(e.target.value)} fullWidth multiline rows={5} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Axes d'amélioration, un par ligne" value={improvements} onChange={(e) => setImprovements(e.target.value)} fullWidth multiline rows={5} />
            </Grid>

            <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>

            {/* 3. Objectifs de saison */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">3. Objectifs de saison</Typography>
                <Button startIcon={<AddIcon />} onClick={() => setSeasonGoals([...seasonGoals, emptyGoal()])}>Ajouter</Button>
              </Box>
            </Grid>
            {seasonGoals.map((goal, index) => (
              <Grid item xs={12} key={index}>
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography sx={{ fontWeight: 'bold' }}>{goal.title || `Objectif ${index + 1}`}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}><TextField label="Titre" value={goal.title} onChange={(e) => updateGoal(index, 'title', e.target.value)} fullWidth /></Grid>
                      <Grid item xs={12} md={3}>
                        <TextField select label="Type" value={goal.type} onChange={(e) => updateGoal(index, 'type', e.target.value)} fullWidth>
                          <MenuItem value="technique">Technique</MenuItem>
                          <MenuItem value="artistic">Artistique</MenuItem>
                          <MenuItem value="personal">Personnel</MenuItem>
                          <MenuItem value="performance">Performance</MenuItem>
                          <MenuItem value="freestyle">Freestyle</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={3}><TextField type="date" label="Deadline" value={goal.deadline} onChange={(e) => updateGoal(index, 'deadline', e.target.value)} fullWidth InputLabelProps={{ shrink: true }} /></Grid>
                      <Grid item xs={10} md={1}><TextField type="number" label="%" value={goal.progress} onChange={(e) => updateGoal(index, 'progress', e.target.value)} fullWidth inputProps={{ min: 0, max: 100 }} /></Grid>
                      <Grid item xs={2} md={1}><IconButton color="error" onClick={() => setSeasonGoals(seasonGoals.filter((_, i) => i !== index))}><DeleteIcon /></IconButton></Grid>
                      <Grid item xs={12}><TextField label="Description" value={goal.description} onChange={(e) => updateGoal(index, 'description', e.target.value)} fullWidth multiline rows={2} /></Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Grid>
            ))}

            {/* 4. Plan d'action */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">4. Plan d'action</Typography>
                <Button startIcon={<AddIcon />} onClick={() => setActionPlan([...actionPlan, emptyAction()])}>Ajouter</Button>
              </Box>
            </Grid>
            {actionPlan.map((action, index) => (
              <Grid item xs={12} key={index}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={3}><TextField label="Objectif" value={action.objective} onChange={(e) => updateAction(index, 'objective', e.target.value)} fullWidth /></Grid>
                  <Grid item xs={12} md={5}><TextField label="Action concrète" value={action.action} onChange={(e) => updateAction(index, 'action', e.target.value)} fullWidth /></Grid>
                  <Grid item xs={10} md={3}><TextField label="Échéance" value={action.deadline} onChange={(e) => updateAction(index, 'deadline', e.target.value)} fullWidth /></Grid>
                  <Grid item xs={2} md={1}><IconButton color="error" onClick={() => setActionPlan(actionPlan.filter((_, i) => i !== index))}><DeleteIcon /></IconButton></Grid>
                </Grid>
              </Grid>
            ))}

            <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>

            {/* 5. Notes */}
            <Grid item xs={12} md={6}>
              <TextField label="Notes visibles côté élève" value={studentNotes} onChange={(e) => setStudentNotes(e.target.value)} fullWidth multiline rows={4} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Notes privées coach/admin" value={privateCoachNotes} onChange={(e) => setPrivateCoachNotes(e.target.value)} fullWidth multiline rows={4} />
            </Grid>
          </Grid>

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button variant="outlined" component={Link} to="/student-evolutions" sx={{ color: '#7C3AED', borderColor: '#7C3AED' }}>
              Annuler
            </Button>
            <Button type="submit" variant="contained" sx={{ backgroundColor: '#7C3AED', '&:hover': { backgroundColor: '#6D28D9' } }}>
              Enregistrer
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default StudentEvolutionForm;